import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import trimText from 'lib/trimText';
import Breakpoints from 'lib/Breakpoints';
import styles from './styles.module.scss';

// can either take two text inputs or it can split one based on character lengths
// can either build text with HTML input or pure string

const textType = {
  MORE_INFO: 'more-info',
  DEK: 'dek',
};

/**
 * TextSummary component displays a summary text block with optional "more info" and related link.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.allowHTMLText - Whether to allow HTML in the text.
 * @param {string} props.dekText - The dek (summary) text.
 * @param {boolean} props.mobileExpand - Whether to expand more info on mobile.
 * @param {string} props.moreInfoClasses - Additional classes for the more info section.
 * @param {string} props.moreInfoText - The "more info" text.
 * @param {string} props.relatedLinkLabel - Label for the related link.
 * @param {string} props.relatedLinkUrl - URL for the related link.
 * @param {string} props.relatedLinkClasses - Additional classes for the related link.
 * @param {boolean} props.openRelatedInNewTab - Whether to open the related link in a new tab.
 * @param {boolean} props.showDek - Whether to show the dek text.
 * @param {boolean} props.showMoreInfo - Whether to show the more info section.
 * @param {string} props.textClasses - Additional classes for the text.
 * @param {boolean} props.trimDekText - Whether to trim the dek text.
 * @returns {JSX.Element|null} The rendered text summary or null if nothing to display.
 */
const TextSummary = ({
  allowHTMLText,
  dekText: dekTextProp,
  mobileExpand,
  moreInfoClasses,
  moreInfoText,
  relatedLinkLabel,
  relatedLinkUrl,
  relatedLinkClasses,
  openRelatedInNewTab,
  showDek,
  showMoreInfo,
  textClasses,
  trimDekText,
}) => {
  const moreInfoElem = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const textClassnames = classNames(
    styles.textSummary,
    textClasses,
  );

  const relatedLinkClassnames = classNames(
    styles.relatedLink,
    relatedLinkClasses,
  );

  useEffect(() => {
    if (moreInfoElem.current) {
      setContentHeight(moreInfoElem.current.scrollHeight);
    }
  }, [moreInfoText, showMoreInfo]);

  useEffect(() => {
    setIsMobile(Breakpoints.isS());

    /**
     * resizeListener is used to update the isMobile state based on window size.
     */
    const resizeListener = () => {
      setIsMobile(Breakpoints.isS());
    };

    window.addEventListener('resize', resizeListener);

    return () => window.removeEventListener('resize', resizeListener);
  }, []);

  if (!showDek && !showMoreInfo) {
    return null;
  }

  // Trim dek if required
  const dekText = showDek && dekTextProp && trimDekText ? trimText(dekTextProp, 60) : dekTextProp;

  /**
   * Adds paragraph breaks to the provided text.
   *
   * @param {string} text - The text to process.
   * @param {string} type - The type of text (DEK or MORE_INFO).
   * @returns {string|JSX.Element[]} HTML string if allowHTMLText, otherwise array of <p> elements.
   */
  const addBreaksToText = (text, type) => {
    if (type === textType.DEK) return text;

    if (allowHTMLText) {
      return text.split('\n').map((paragraph) => (
        `<p>${paragraph}</p>`)).join('');
    }

    return text.split('\n').map((paragraph) => (
      <p>
        {paragraph}
      </p>
    ));
  };

  /**
   *
   * Builds a <div> element with appropriate attributes and content for the text summary.
   *
   * @param {string} text - The text to display.
   * @param {string} type - The type of text (DEK or MORE_INFO).
   * @returns {JSX.Element} The rendered div element.
   */
  const buildPTag = (text, type) => {
    const pTagAttributes = {
      'data-test': `text-summary--${type}`,
      'data-component': `text-summary--${type}`,
      'data-testid': `text-summary--${type}`,
      className: textClassnames,
    };
    if (type === textType.MORE_INFO) {
      /**
       * Ref callback to assign the more info element to the ref.
       *
       * @param {HTMLElement} element - The DOM element for the more info section.
       */
      pTagAttributes.ref = (element) => { moreInfoElem.current = element; };
      pTagAttributes.className += ` ${moreInfoClasses}`;
    }

    const textWithBreaks = addBreaksToText(text, type);

    if (allowHTMLText) {
      pTagAttributes.dangerouslySetInnerHTML = { __html: textWithBreaks };
    } else {
      pTagAttributes.children = textWithBreaks;
    }

    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <div {...pTagAttributes} />
    );
  };

  const dekTextParagraph = showDek && dekText !== ''
    ? buildPTag(dekText, textType.DEK) : null;

  const showMoreTextParagraph = showMoreInfo && moreInfoText !== ''
    ? buildPTag(moreInfoText, textType.MORE_INFO) : null;

  const showRelatedLink = relatedLinkLabel && relatedLinkUrl
    ? (
      <p className={relatedLinkClassnames}>
        <a href={relatedLinkUrl} target={openRelatedInNewTab ? '_blank' : ''} rel="noreferrer">
          {relatedLinkLabel}
          {openRelatedInNewTab && <span className="icon icon-external-link" />}
        </a>
      </p>
    )
    : null;

  return (
    <div>
      {dekTextParagraph}
      {showMoreTextParagraph}
      {showRelatedLink}
      {mobileExpand && showMoreInfo && moreInfoText !== '' && isMobile && (
        <div
          data-testid="text-summary--more-info--mobile"
          className={classNames(
            'overflow-hidden dn-m',
            styles.moreInfoWrapper,
            { 'o-0': !showMoreInfo, 'o-100': showMoreInfo },
          )}
          style={{ height: (showMoreInfo && contentHeight) || 0 }}
        >
          {showMoreTextParagraph}
        </div>
      )}
    </div>
  );
};

export { TextSummary };
